/**
 * Watches the card-generation run a save started and says how it went.
 *
 * Separable on purpose: it takes only a run id and a deck, so task 6's text screen
 * takes it over unchanged. It is not a progress screen of its own: it reads text
 * import's status endpoint (useTextImportStatus) and, when anything is short of a
 * clean finish, points to text import's existing status page for the details. A
 * failure is never silent (the brief); every outcome the status page names has a
 * line here.
 */
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { useLocale } from '../../i18n/context'
import { useTextImportStatus } from '../../hooks/useTextImport'

interface Props {
  textImportId: string
  deckId: string | null
  title: string | null
}

export default function GenerationWatcher({ textImportId, deckId, title }: Props) {
  const { tr } = useLocale()
  const s = tr.textFlow.saved
  const { data, isError } = useTextImportStatus(textImportId)

  const detailsLink = (
    <Link
      to={`/text-import/${textImportId}/status`}
      state={{ deck_id: deckId, title }}
      style={{ color: 'var(--accent)', fontWeight: 600 }}
    >
      {s.genDetails}
    </Link>
  )

  let icon = <Loader2 size={18} aria-hidden className="animate-spin" style={{ color: 'var(--accent)' }} />
  let text: string = s.generatingStart
  let problem = false
  let details = false

  if (isError && !data) {
    icon = <AlertCircle size={18} aria-hidden style={{ color: 'var(--danger)' }} />
    text = s.statusUnreachable
    problem = true
  } else if (data) {
    const total = data.words_confirmed - data.words_skipped
    if (data.status === 'failed') {
      icon = <AlertCircle size={18} aria-hidden style={{ color: 'var(--danger)' }} />
      text = s.genFailed
      problem = details = true
    } else if (data.status === 'done') {
      if (data.words_failed > 0) {
        icon = <AlertCircle size={18} aria-hidden style={{ color: 'var(--danger)' }} />
        text = s.genPartial
        problem = details = true
      } else if (data.words_pending > 0) {
        icon = <CheckCircle2 size={18} aria-hidden style={{ color: 'var(--success)' }} />
        text = s.genPending
        details = true
      } else {
        icon = <CheckCircle2 size={18} aria-hidden style={{ color: 'var(--success)' }} />
        text = s.generated
      }
    } else if (total > 0) {
      text = s.generating(data.words_generated, total)
    }
  }

  return (
    <div
      role={problem ? 'alert' : 'status'}
      data-testid="generation-watcher"
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12,
        background: 'var(--bg-surface)', border: '1px solid var(--border)', fontSize: 14,
        color: 'var(--text-primary)',
      }}
    >
      {icon}
      <span style={{ flex: 1 }}>{text}</span>
      {details && detailsLink}
    </div>
  )
}
